import React, { useState } from 'react';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { Loader2, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/api.service';
import { API_ENDPOINTS } from '../config/api';

interface SuggestionBoxProps {
    trigger?: React.ReactNode;
}

export function SuggestionBox({ trigger }: SuggestionBoxProps) {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        type: 'suggestion',
        subject: '',
        message: '',
        studentName: '',
        studentEmail: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTypeChange = (value: string) => {
        setFormData(prev => ({ ...prev, type: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const payload = { ...formData };

            // Uses the shared apiClient so the auth token is attached — the
            // /complaints endpoint now requires an authenticated, booked student.
            const response = await apiClient.post(API_ENDPOINTS.COMPLAINTS.CREATE, payload);

            // Axios only resolves on a 2xx response, so treat any success status as sent.
            toast.success(response.data?.message || 'Message sent. Thank you for your feedback!');
            setOpen(false);
            setFormData({
                type: 'suggestion',
                subject: '',
                message: '',
                studentName: '',
                studentEmail: ''
            });
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to send message. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button variant="outline" size="sm" className="gap-2">
                        <MessageSquarePlus className="h-4 w-4" />
                        Suggestion Box
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Complaint & Suggestion Box</DialogTitle>
                    <DialogDescription>
                        We value your feedback. Let us know how we can improve your stay.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="type">I want to send a...</Label>
                        <Select value={formData.type} onValueChange={handleTypeChange}>
                            <SelectTrigger id="type" className="w-full">
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem 
                                    value="suggestion" 
                                    className="w-full cursor-pointer py-3 px-4 data-[state=checked]:bg-[#d4af37]/10 hover:bg-gray-100"
                                >
                                    Suggestion
                                </SelectItem>
                                <SelectItem 
                                    value="complaint" 
                                    className="w-full cursor-pointer py-3 px-4 data-[state=checked]:bg-[#d4af37]/10 hover:bg-gray-100"
                                >
                                    Complaint
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="studentName">Name</Label>
                            <Input
                                id="studentName"
                                name="studentName"
                                placeholder="Your name"
                                value={formData.studentName}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="studentEmail">Email (Optional)</Label>
                            <Input
                                id="studentEmail"
                                name="studentEmail"
                                type="email"
                                placeholder="For us to reply"
                                value={formData.studentEmail}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                            id="subject"
                            name="subject"
                            placeholder="What is this about?"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                            id="message"
                            name="message"
                            placeholder="Tell us more details..."
                            className="h-[180px]"
                            value={formData.message}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <DialogFooter>
                        <Button type="submit" disabled={isLoading} variant="gold">
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Send Message'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
